<template>
  <div class="dtp-table">
    <div class="dtp-table__filters">
      <div class="dtp-table__search">
        <v-text-field
          v-model="searchString"
          class="dtp-table__search-field"
          solo
          :placeholder="$t('dtp.placeholder')"
        />

        <v-btn
          type="button"
          class="search-btn warning mb-4"
          depressed
          large
          @click="filters.search = searchString"
        >
          {{ $t('common.btns.search') }}
        </v-btn>
      </div>

      <v-menu
        ref="menuDate"
        :close-on-content-click="false"
        v-model="menuDate"
        :return-value.sync="filters.dates"
        max-width="280px"
        min-width="280px"
        class="pr-4 mt-2"
        transition="scale-transition"
      >
        <template v-slot:activator="{ on }">
          <v-text-field
            v-on="on"
            v-model="filters.dates"
            prepend-icon="mdi-calendar"
            :label="$t('common.fields.rentDate')"
            readonly
            class="dtp-table__dates"
          ></v-text-field>
        </template>
        <v-date-picker
          v-model="filters.dates"
          color="primary lighten-1"
          width="280"
          range
          :locale="$i18n.locale"
          :selected-items-text="filters.dates.toString()"
          :allowed-dates="allowedDates"
          @input="changeDate"
        ></v-date-picker>
      </v-menu>

      <v-select
        class="dtp-table__status"
        item-text="name"
        item-value="id"
        multiple
        clearable
        :value="filters.status"
        :items="statuses"
        :item-disabled="isStatusDisabled"
        :loading="propertiesLoading"
        :label="$t('common.fields.status')"
        @change="onStatusChange"
      >
        <template #selection="{ index }">
          <template v-if="index > 0"></template>
          <template v-else>
            {{ filters.status.length }}
            {{ $t('common.selected') }}
          </template>
        </template>
      </v-select>

      <v-btn
        v-if="visibleColumns"
        class="ml-auto mt-4"
        icon
        @click="settingsDialog = true"
      >
        <v-icon>mdi-cog</v-icon>
      </v-btn>
    </div>

    <v-data-table
      ref="dtp-table"
      class="dtp-table__list mt-3"
      :height="tableHeight"
      :headers="activeHeaders"
      :loading="loading"
      :items="list"
      :items-per-page="perPage"
      hide-default-header
      hide-default-footer
    >
      <template #header="{ props }">
        <thead class="v-data-table-header">
          <tr>
            <template v-for="(header, key) in props.headers">
              <th
                class="text-align-center"
                :class="{ section: header.sections }"
                :style="{ minWidth: header.width || 'auto' }"
                :key="`header-${key}`"
                :colspan="header.sections ? header.sections.length : 1"
              >
                <template v-if="header.sections">
                  {{ header.text }}
                </template>
              </th>
            </template>
          </tr>

          <tr>
            <template v-for="(header, key) in props.headers">
              <template v-if="header.sections">
                <th
                  v-for="(subheader, idx) in header.sections"
                  :key="`subheader-${key}-${idx}`"
                  class="section"
                  :class="{ last: idx === header.sections.length - 1 }"
                >
                  {{ subheader.text }}
                </th>
              </template>

              <th :key="key" v-else>
                {{ header.text }}
                <v-btn
                  v-if="header.sortParam"
                  color="rgba(255, 255, 255, 0.8)"
                  class="dtp-table__sort-btn"
                  icon
                  small
                  :class="
                    filters.orderCol === header.sortParam
                      ? filters.orderType
                      : 'asc'
                  "
                  @click="
                    () => {
                      filters.orderCol = header.sortParam;
                      filters.orderType =
                        filters.orderType === 'asc' ? 'desc' : 'asc';
                    }
                  "
                >
                  <v-icon
                    :color="
                      filters.orderCol === header.sortParam ? 'warning' : ''
                    "
                    size="18"
                  >
                    mdi-arrow-up
                  </v-icon>
                </v-btn>
              </th>
            </template>
          </tr>
        </thead>
      </template>

      <template #item="{ item }">
        <tr :class="{ updating: updatingItems.includes(item.id) }">
          <td>{{ item.num }}</td>
          <td>
            <v-tooltip top>
              <template v-slot:activator="{ on, attrs }">
                <v-btn
                  color="blue"
                  v-bind="attrs"
                  v-on="on"
                  icon
                  small
                  @click="
                    () => {
                      photosDialog.opened = true;
                      photosDialog.itemId = item.id;
                    }
                  "
                >
                  <v-icon size="18">mdi-image-multiple</v-icon>
                </v-btn>
              </template>

              {{ $t('dtp.photos') }}
            </v-tooltip>
          </td>
          <td>{{ item.created_at }}</td>
          <td>
            <edit-on-click-field :label="item.car.number" :disabled="noEdit">
              <auto-select
                class="mb-2"
                style="min-width:90px"
                :number="item.car.number"
                hide-details
                autofocus
                @change="updateItem({ id: item.id, params: $event })"
              />
            </edit-on-click-field>
          </td>
          <td v-if="isActiveColumn('dtp-phone')">
            <edit-on-click-field
              :label="item.driver.user.phone"
              :disabled="noEdit"
            >
              <driver-select
                class="mb-2"
                :phone="item.driver.user.phone"
                :clearable="false"
                hide-details
                autofocus
                @change="updateItem({ id: item.id, params: $event })"
              />
            </edit-on-click-field>
          </td>
          <td v-if="isActiveColumn('dtp-name')">{{ item.driver.user.name }}</td>
          <td v-if="isActiveColumn('dtp-status')">
            <edit-on-click-field
              :label="getStatus(item.status)"
              :disabled="noEdit"
            >
              <v-select
                class="mb-2"
                item-text="name"
                item-value="id"
                :value="item.status"
                :items="properties.car_statuses"
                dense
                hide-details
                autofocus
                @change="
                  updateItem({ id: item.id, params: { status: $event } })
                "
              />
            </edit-on-click-field>
          </td>
          <td v-if="isActiveColumn('dtp-date')">
            <edit-on-click-field
              :label="item.accident_date"
              :disabled="noEdit"
              nowrap
            >
              <menu-datepicker
                label=""
                :value="item.accident_date"
                autofocus
                @change="
                  updateItem({
                    id: item.id,
                    params: { accident_date: $event },
                  })
                "
              />
            </edit-on-click-field>
          </td>
          <td v-if="isActiveColumn('dtp-culprit')">
            <edit-on-click-field
              :label="getAccidentCauser(item.accident_causer)"
              :disabled="noEdit"
            >
              <v-select
                class="mb-2"
                item-text="name"
                item-value="id"
                :value="item.accident_causer"
                dense
                hide-details
                autofocus
                :items="properties.accident_causers"
                @change="
                  updateItem({
                    id: item.id,
                    params: { accident_causer: $event },
                  })
                "
              />
            </edit-on-click-field>
          </td>
          <td v-if="isActiveColumn('dtp-damage-description')">
            <button
              class="edit-btn"
              :disabled="noEdit"
              @click="
                () => {
                  editDialog.opened = true;
                  editDialog.param = 'damage_description';
                  editDialog.label = $t('dtp.damageDescription');
                  editDialog.value = item.damage_description;
                  editDialog.itemId = item.id;
                  editDialog.type = 'text';
                }
              "
            >
              {{ item.damage_description || '—' }}
            </button>
          </td>
          <td
            v-if="isActiveColumn('dtp-hidden')"
            :class="getTypeClass(item.accident_hide)"
          >
            <edit-on-click-field
              :label="getType(item.accident_hide)"
              :disabled="noEdit"
            >
              <type-select
                class="mb-2"
                :value="item.accident_hide"
                dense
                hide-details
                @change="
                  updateItem({
                    id: item.id,
                    params: { accident_hide: $event },
                  })
                "
              />
            </edit-on-click-field>
          </td>
          <td v-if="isActiveColumn('dtp-period-restoration')">
            <button
              class="edit-btn"
              :disabled="noEdit"
              @click="
                () => {
                  editDialog.opened = true;
                  editDialog.param = 'period_restoration';
                  editDialog.label = $t('dtp.period_restoration');
                  editDialog.value = item.period_restoration;
                  editDialog.itemId = item.id;
                  editDialog.type = 'text';
                }
              "
            >
              {{ item.period_restoration || '—' }}
            </button>
          </td>
          <td
            v-if="isActiveColumn('dtp-narcology')"
            :class="getTypeClass(item.drug_test)"
          >
            <edit-on-click-field
              :label="getType(item.drug_test)"
              :disabled="noEdit"
            >
              <type-select
                class="mb-2"
                :value="item.drug_test"
                dense
                hide-details
                @change="
                  updateItem({
                    id: item.id,
                    params: { drug_test: $event },
                  })
                "
              />
            </edit-on-click-field>
          </td>
          <td v-if="isActiveColumn('dtp-narcology-request-date')">
            <button
              class="edit-btn"
              :disabled="noEdit"
              @click="
                () => {
                  dateTimeDialog.opened = true;
                  dateTimeDialog.param = 'drug_conclusion_datetime';
                  dateTimeDialog.value = item.drug_conclusion_datetime;
                  dateTimeDialog.itemId = item.id;
                }
              "
            >
              {{ item.drug_conclusion_datetime || '—' }}
            </button>
          </td>
          <td
            v-if="isActiveColumn('dtp-narcology-conclusion')"
            :class="getTypeClass(item.drug_conclusion)"
          >
            <edit-on-click-field
              :label="getType(item.drug_conclusion)"
              :disabled="noEdit"
            >
              <type-select
                class="mb-2"
                :value="item.drug_conclusion"
                dense
                hide-details
                @change="
                  updateItem({
                    id: item.id,
                    params: { drug_conclusion: $event },
                  })
                "
              />
            </edit-on-click-field>
          </td>
          <td v-if="isActiveColumn('dtp-narcology-conclusion-date')">
            <button
              class="edit-btn"
              :disabled="noEdit"
              @click="
                () => {
                  dateTimeDialog.opened = true;
                  dateTimeDialog.param = 'drug_report_datetime';
                  dateTimeDialog.value = item.drug_report_datetime;
                  dateTimeDialog.itemId = item.id;
                }
              "
            >
              {{ item.drug_report_datetime || '—' }}
            </button>
          </td>
          <td
            v-if="isActiveColumn('dtp-insurance-request')"
            :class="getTypeClass(item.is_insurance)"
          >
            <edit-on-click-field
              :label="getType(item.is_insurance)"
              :disabled="noEdit"
            >
              <v-select
                class="mb-2"
                item-text="name"
                item-value="id"
                dense
                hide-details
                :value="item.is_insurance"
                :items="properties.selects"
                @change="
                  updateItem({
                    id: item.id,
                    params: { is_insurance: $event },
                  })
                "
              />
            </edit-on-click-field>
          </td>
          <td v-if="isActiveColumn('dtp-insurance-name')">
            <button
              class="edit-btn"
              :disabled="noEdit"
              @click="
                () => {
                  editDialog.opened = true;
                  editDialog.param = 'insurance_name';
                  editDialog.label = $t('dtp.insuranceName');
                  editDialog.value = item.insurance_name;
                  editDialog.itemId = item.id;
                  editDialog.type = 'text';
                }
              "
            >
              {{ item.insurance_name || '—' }}
            </button>
          </td>
          <td v-if="isActiveColumn('dtp-insurance-request-date')">
            <edit-on-click-field
              :label="item.insurance_application_date || '—'"
              :disabled="noEdit"
            >
              <menu-datepicker
                label=""
                :value="item.insurance_application_date"
                autofocus
                @change="
                  updateItem({
                    id: item.id,
                    params: { insurance_application_date: $event },
                  })
                "
              />
            </edit-on-click-field>
          </td>
          <td v-if="isActiveColumn('dtp-insurance-type')">
            <edit-on-click-field
              :label="getInsuranceType(item.is_insurance_our)"
              :disabled="noEdit"
            >
              <v-select
                class="mb-2"
                item-text="name"
                item-value="id"
                :value="item.is_insurance_our"
                dense
                hide-details
                autofocus
                :items="properties.insurances"
                @change="
                  updateItem({
                    id: item.id,
                    params: { is_insurance_our: $event },
                  })
                "
              />
            </edit-on-click-field>
          </td>
          <td
            v-if="isActiveColumn('dtp-materials')"
            :class="getTypeClass(item.referral_assessment_materials)"
          >
            <edit-on-click-field
              :label="getType(item.referral_assessment_materials)"
              :disabled="noEdit"
            >
              <type-select
                class="mb-2"
                :value="item.referral_assessment_materials"
                dense
                hide-details
                @change="
                  updateItem({
                    id: item.id,
                    params: { referral_assessment_materials: $event },
                  })
                "
              />
            </edit-on-click-field>
          </td>
          <td v-if="isActiveColumn('dtp-materials-receiving-date')">
            <edit-on-click-field
              :label="item.referral_assessment_materials_date || '—'"
              :disabled="noEdit"
            >
              <menu-datepicker
                label=""
                :value="item.referral_assessment_materials_date"
                autofocus
                @change="
                  updateItem({
                    id: item.id,
                    params: { referral_assessment_materials_date: $event },
                  })
                "
              />
            </edit-on-click-field>
          </td>
          <td
            v-if="isActiveColumn('dtp-assessment-referral')"
            :class="getTypeClass(item.referral_assessment_assessment)"
          >
            <edit-on-click-field
              :label="getType(item.referral_assessment_assessment)"
              :disabled="noEdit"
            >
              <type-select
                class="mb-2"
                :value="item.referral_assessment_assessment"
                dense
                hide-details
                @change="
                  updateItem({
                    id: item.id,
                    params: { referral_assessment_assessment: $event },
                  })
                "
              />
            </edit-on-click-field>
          </td>
          <td v-if="isActiveColumn('dtp-assessment-receiving-date')">
            <edit-on-click-field
              :label="item.referral_assessment_assessment_date || '—'"
              :disabled="noEdit"
            >
              <menu-datepicker
                label=""
                :value="item.referral_assessment_assessment_date"
                autofocus
                @change="
                  updateItem({
                    id: item.id,
                    params: { referral_assessment_assessment_date: $event },
                  })
                "
              />
            </edit-on-click-field>
          </td>
          <td v-if="isActiveColumn('dtp-assessment-name')">
            <button
              class="edit-btn"
              :disabled="noEdit"
              @click="
                () => {
                  editDialog.opened = true;
                  editDialog.param = 'referral_assessment_company';
                  editDialog.label = $t('dtp.assessmentName');
                  editDialog.value = item.referral_assessment_company;
                  editDialog.itemId = item.id;
                  editDialog.type = 'text';
                }
              "
            >
              {{ item.referral_assessment_company || '—' }}
            </button>
          </td>
          <td
            v-if="isActiveColumn('dtp-grade')"
            :class="getTypeClass(item.assessment)"
          >
            <edit-on-click-field
              :label="getType(item.assessment)"
              :disabled="noEdit"
            >
              <type-select
                class="mb-2"
                :value="item.assessment"
                dense
                hide-details
                @change="
                  updateItem({
                    id: item.id,
                    params: { assessment: $event },
                  })
                "
              />
            </edit-on-click-field>
          </td>
          <td v-if="isActiveColumn('dtp-grade-pass-date')">
            <edit-on-click-field
              :label="item.assessment_date || '—'"
              :disabled="noEdit"
            >
              <menu-datepicker
                label=""
                :value="item.assessment_date"
                autofocus
                @change="
                  updateItem({
                    id: item.id,
                    params: { assessment_date: $event },
                  })
                "
              />
            </edit-on-click-field>
          </td>
          <td v-if="isActiveColumn('dtp-grade-report-date')">
            <edit-on-click-field
              :label="item.assessment_report_date || '—'"
              :disabled="noEdit"
            >
              <menu-datepicker
                label=""
                :value="item.assessment_report_date"
                autofocus
                @change="
                  updateItem({
                    id: item.id,
                    params: { assessment_report_date: $event },
                  })
                "
              />
            </edit-on-click-field>
          </td>
          <td v-if="isActiveColumn('dtp-grade-amount')">
            <button
              class="edit-btn"
              :disabled="noEdit"
              @click="
                () => {
                  editDialog.opened = true;
                  editDialog.param = 'assessment_amount';
                  editDialog.label = $t('dtp.gradeAmount');
                  editDialog.value = Number(item.assessment_amount);
                  editDialog.itemId = item.id;
                  editDialog.type = 'number';
                }
              "
            >
              {{
                item.assessment_amount
                  ? formatAmount(item.assessment_amount)
                  : '—'
              }}
            </button>
          </td>
          <td
            v-if="isActiveColumn('dtp-auto-recovery')"
            :class="getTypeClass(item.auto_restoration)"
          >
            <edit-on-click-field
              :label="getType(item.auto_restoration)"
              :disabled="noEdit"
            >
              <type-select
                class="mb-2"
                :value="item.auto_restoration"
                dense
                hide-details
                @change="
                  updateItem({
                    id: item.id,
                    params: { auto_restoration: $event },
                  })
                "
              />
            </edit-on-click-field>
          </td>
          <td v-if="isActiveColumn('dtp-troubleshooting-type')">
            <edit-on-click-field
              :label="getRestorationType(item.auto_restoration_type)"
              :disabled="noEdit"
            >
              <v-select
                class="mb-2"
                item-text="name"
                item-value="id"
                :value="item.auto_restoration_type"
                dense
                hide-details
                autofocus
                :items="properties.repair_types"
                @change="
                  updateItem({
                    id: item.id,
                    params: { auto_restoration_type: $event },
                  })
                "
              />
            </edit-on-click-field>
          </td>
          <td v-if="isActiveColumn('dtp-troubleshooting-date')">
            <edit-on-click-field
              :label="item.auto_troubleshooting_date || '—'"
              :disabled="noEdit"
            >
              <menu-datepicker
                label=""
                :value="item.auto_troubleshooting_date"
                autofocus
                @change="
                  updateItem({
                    id: item.id,
                    params: { auto_troubleshooting_date: $event },
                  })
                "
              />
            </edit-on-click-field>
          </td>
          <td v-if="isActiveColumn('dtp-troubleshooting-amount')">
            <button
              class="edit-btn"
              :disabled="noEdit"
              @click="
                () => {
                  editDialog.opened = true;
                  editDialog.param = 'auto_troubleshooting_amount';
                  editDialog.label = $t('dtp.troubleshootingAmount');
                  editDialog.value = Number(item.auto_troubleshooting_amount);
                  editDialog.itemId = item.id;
                  editDialog.type = 'number';
                }
              "
            >
              {{
                item.auto_troubleshooting_amount
                  ? formatAmount(item.auto_troubleshooting_amount)
                  : '—'
              }}
            </button>
          </td>
          <td
            v-if="isActiveColumn('dtp-coordination-with-owner')"
            :class="getTypeClass(item.owner_price_accept)"
          >
            <edit-on-click-field
              :label="getType(item.owner_price_accept)"
              :disabled="noEdit"
            >
              <type-select
                class="mb-2"
                :value="item.owner_price_accept"
                dense
                hide-details
                @change="
                  updateItem({
                    id: item.id,
                    params: { owner_price_accept: $event },
                  })
                "
              />
            </edit-on-click-field>
          </td>
          <td v-if="isActiveColumn('dtp-ready-status')">
            <edit-on-click-field
              :label="getRestorationStatus(item.auto_restoration_status)"
              :disabled="noEdit"
            >
              <v-select
                class="mb-2"
                item-text="name"
                item-value="id"
                :value="item.auto_restoration_status"
                dense
                hide-details
                autofocus
                :items="properties.auto_restoration_statuses"
                @change="
                  updateItem({
                    id: item.id,
                    params: { auto_restoration_status: $event },
                  })
                "
              />
            </edit-on-click-field>
          </td>
          <td v-if="isActiveColumn('dtp-troubleshooting-end-date')">
            <edit-on-click-field
              :label="item.auto_troubleshooting_date || '—'"
              :disabled="noEdit"
            >
              <menu-datepicker
                label=""
                :value="item.auto_troubleshooting_end_date"
                autofocus
                @change="
                  updateItem({
                    id: item.id,
                    params: { auto_troubleshooting_end_date: $event },
                  })
                "
              />
            </edit-on-click-field>
          </td>
          <td
            v-if="isActiveColumn('dtp-pay')"
            :class="getTypeClass(item.payout)"
          >
            <edit-on-click-field
              :label="getType(item.payout)"
              :disabled="noEdit"
            >
              <type-select
                class="mb-2"
                :value="item.payout"
                dense
                hide-details
                @change="
                  updateItem({
                    id: item.id,
                    params: { payout: $event },
                  })
                "
              />
            </edit-on-click-field>
          </td>
          <td v-if="isActiveColumn('dtp-court-statement')">
            <edit-on-click-field
              :label="item.tribunal_act_date || '—'"
              :disabled="noEdit"
            >
              <menu-datepicker
                label=""
                :value="item.tribunal_act_date"
                autofocus
                @change="
                  updateItem({
                    id: item.id,
                    params: { tribunal_act_date: $event },
                  })
                "
              />
            </edit-on-click-field>
          </td>
          <td v-if="isActiveColumn('dtp-payment-application-date')">
            <edit-on-click-field
              :label="item.application_declaration_payout_date || '—'"
              :disabled="noEdit"
            >
              <menu-datepicker
                label=""
                :value="item.application_declaration_payout_date"
                autofocus
                @change="
                  updateItem({
                    id: item.id,
                    params: { application_declaration_payout_date: $event },
                  })
                "
              />
            </edit-on-click-field>
          </td>
          <td v-if="isActiveColumn('dtp-payment-date')">
            <edit-on-click-field
              :label="item.payout_date || '—'"
              :disabled="noEdit"
            >
              <menu-datepicker
                label=""
                :value="item.payout_date"
                autofocus
                @change="
                  updateItem({
                    id: item.id,
                    params: { payout_date: $event },
                  })
                "
              />
            </edit-on-click-field>
          </td>
          <td v-if="isActiveColumn('dtp-payment-amount')">
            <button
              class="edit-btn"
              :disabled="noEdit"
              @click="
                () => {
                  editDialog.opened = true;
                  editDialog.param = 'payout_amount';
                  editDialog.label = $t('dtp.paymentAmount');
                  editDialog.value = Number(item.payout_amount);
                  editDialog.itemId = item.id;
                  editDialog.type = 'number';
                }
              "
            >
              {{ item.payout_amount ? formatAmount(item.payout_amount) : '—' }}
            </button>
          </td>
        </tr>
      </template>

      <template #footer>
        <v-divider />
        <div class="pt-1 pb-2">
          <v-pagination
            color="warning"
            :value="currentPage"
            :length="lastPage"
            dark
            circle
            @input="onPageChange"
          />
        </div>
      </template>
    </v-data-table>

    <text-field-edit-dialog
      v-model="editDialog.opened"
      :param-name="editDialog.param"
      :label="editDialog.label"
      :currentValue="editDialog.value"
      :type="editDialog.type"
      @save="
        updateItem({
          id: editDialog.itemId,
          params: $event,
        })
      "
    />

    <date-time-picker
      v-model="dateTimeDialog.opened"
      :date-time="dateTimeDialog.value"
      @change="
        updateItem({
          id: dateTimeDialog.itemId,
          params: { [dateTimeDialog.param]: $event },
        })
      "
    />

    <dtp-table-settings-dialog
      v-if="visibleColumns"
      v-model="settingsDialog"
      :visible-columns="visibleColumns"
      @update="$emit('visible-columns-update', $event)"
    />

    <dtp-photos-dialog
      v-model="photosDialog.opened"
      :item-id="photosDialog.itemId"
      @save="
        updateItem({
          id: photosDialog.itemId,
          params: { photos: $event },
        })
      "
    />

    <v-snackbar v-model="snack.opened" :color="snack.color" bottom right>
      {{ snack.message }}
    </v-snackbar>
  </div>
</template>

<script>
import { mapState } from 'vuex';
import EditOnClickField from '@/components/shared/EditOnClickField.vue';
import AutoSelect from '@/components/shared/AutoSelect.vue';
import DriverSelect from '@/components/shared/DriverSelect.vue';
import MenuDatepicker from '@/components/shared/MenuDatepicker.vue';
import TextFieldEditDialog from '@/components/shared/TextFieldEditDialog.vue';
import DtpTableSettingsDialog from '@/components/dtp/DtpTableSettingsDialog.vue';
import DtpPhotosDialog from '@/components/dtp/DtpPhotosDialog.vue';
import TypeSelect from '@/components/dtp/TypeSelect.vue';
import DateTimePicker from '@/components/shared/DateTimePicker.vue';
import formatAmount from '@/helpers/formatAmount';
import calcTableHeight from '@/helpers/calcTableHeight';

export default {
  components: {
    EditOnClickField,
    AutoSelect,
    DriverSelect,
    MenuDatepicker,
    TextFieldEditDialog,
    TypeSelect,
    DateTimePicker,
    DtpTableSettingsDialog,
    DtpPhotosDialog,
  },
  props: {
    carId: {
      type: String,
      default: '',
    },
    driverId: {
      type: String,
      default: '',
    },
    simple: {
      type: Boolean,
      default: false,
    },
    visibleColumns: {
      default: null,
    },
  },
  data: () => ({
    loading: false,
    propertiesLoading: false,
    menuDate: false,
    settingsDialog: false,
    searchString: '',
    filters: {
      search: '',
      dates: [],
      status: [],
      orderCol: '',
      orderType: 'desc',
    },
    editDialog: {
      opened: false,
      param: '',
      label: '',
      value: '',
      itemId: null,
      type: '',
    },
    dateTimeDialog: {
      opened: false,
      param: '',
      value: '',
      itemId: null,
    },
    photosDialog: {
      opened: false,
      itemId: '',
    },
    snack: {
      opened: false,
      color: '',
      message: '',
    },
    updatingItems: [],
    tableHeight: 'auto',
  }),
  watch: {
    filters: {
      deep: true,
      async handler(params) {
        if (params.dates.length === 1) return;

        this.loading = true;
        await this.fetchList(params);
        this.loading = false;
      },
    },
  },
  computed: {
    ...mapState({
      list: ({ dtp }) => dtp.list,
      currentPage: ({ dtp }) => dtp.currentPage,
      lastPage: ({ dtp }) => dtp.lastPage,
      perPage: ({ dtp }) => dtp.perPage,
      properties: ({ dtp }) => dtp.properties,
    }),
    headers() {
      return [
        { text: this.$t('common.number'), width: '60px' },
        { text: '', width: '60px' },
        { text: this.$t('dtp.createdDate'), width: '195px' },
        { text: this.$t('auto.fields.number') },
        { text: this.$t('common.fields.phone'), key: 'dtp-phone' },
        { text: this.$t('common.fields.fullName'), key: 'dtp-name' },
        {
          text: this.$t('common.fields.status'),
          key: 'dtp-status',
          sortParam: 'status',
        },
        {
          text: this.$t('dtp.date'),
          key: 'dtp-date',
          sortParam: 'accident_date',
        },
        { text: this.$t('dtp.culprit'), key: 'dtp-culprit' },
        {
          text: this.$t('dtp.damageDescription'),
          key: 'dtp-damage-description',
        },
        { text: this.$t('dtp.isHidden'), key: 'dtp-hidden' },
        {
          text: this.$t('dtp.period_restoration'),
          key: 'dtp-period-restoration',
        },
        {
          text: this.$t('dtp.narcology'),
          sections: [
            { text: this.$t('dtp.narcology'), key: 'dtp-narcology' },
            {
              text: this.$t('dtp.requestDate'),
              key: 'dtp-narcology-request-date',
            },
            {
              text: this.$t('dtp.conclusion'),
              key: 'dtp-narcology-conclusion',
            },
            {
              text: this.$t('dtp.conclusionDate'),
              key: 'dtp-narcology-conclusion-date',
            },
          ],
        },
        {
          text: this.$t('dtp.insurance'),
          sections: [
            {
              text: this.$t('dtp.insuranceRequest'),
              key: 'dtp-insurance-request',
            },
            { text: this.$t('dtp.insuranceName'), key: 'dtp-insurance-name' },
            {
              text: this.$t('dtp.insuranceRequestDate'),
              key: 'dtp-insurance-request-date',
            },
            { text: this.$t('dtp.insuranceType'), key: 'dtp-insurance-type' },
          ],
        },
        {
          text: this.$t('dtp.materials'),
          sections: [
            { text: this.$t('dtp.materials'), key: 'dtp-materials' },
            {
              text: this.$t('dtp.receivingDate'),
              key: 'dtp-materials-receiving-date',
            },
          ],
        },
        {
          text: this.$t('dtp.assessmentReferral'),
          sections: [
            {
              text: this.$t('dtp.assessmentReferral'),
              key: 'dtp-assessment-referral',
            },
            {
              text: this.$t('dtp.receivingDate'),
              key: 'dtp-assessment-receiving-date',
            },
            { text: this.$t('dtp.assessmentName'), key: 'dtp-assessment-name' },
          ],
        },
        {
          text: this.$t('dtp.grade'),
          sections: [
            { text: this.$t('dtp.grade'), key: 'dtp-grade' },
            { text: this.$t('dtp.passDate'), key: 'dtp-grade-pass-date' },
            { text: this.$t('dtp.reportDate'), key: 'dtp-grade-report-date' },
            { text: this.$t('dtp.gradeAmount'), key: 'dtp-grade-amount' },
          ],
        },
        {
          text: this.$t('dtp.autoRecovery'),
          sections: [
            { text: this.$t('dtp.autoRecovery'), key: 'dtp-auto-recovery' },
            {
              text: this.$t('dtp.troubleshootingType'),
              key: 'dtp-troubleshooting-type',
            },
            {
              text: this.$t('dtp.troubleshootingDate'),
              key: 'dtp-troubleshooting-date',
            },
            {
              text: this.$t('dtp.troubleshootingAmount'),
              key: 'dtp-troubleshooting-amount',
            },
            {
              text: this.$t('dtp.coordinationWithOwner'),
              key: 'dtp-coordination-with-owner',
            },
            { text: this.$t('dtp.readyStatus'), key: 'dtp-ready-status' },
            {
              text: this.$t('dtp.troubleshootingEndDate'),
              key: 'dtp-troubleshooting-end-date',
            },
          ],
        },
        {
          text: this.$t('dtp.pay'),
          sections: [
            { text: this.$t('dtp.pay'), key: 'dtp-pay' },
            { text: this.$t('dtp.courtStatement'), key: 'dtp-court-statement' },
            {
              text: this.$t('dtp.paymentApplicationDate'),
              key: 'dtp-payment-application-date',
            },
            { text: this.$t('dtp.paymentDate'), key: 'dtp-payment-date' },
            { text: this.$t('dtp.paymentAmount'), key: 'dtp-payment-amount' },
          ],
        },
      ];
    },
    activeHeaders() {
      return this.headers.reduce((res, item) => {
        const header = { ...item };

        if (!this.isActiveColumn(header.key)) return res;

        if (header.sections) {
          // eslint-disable-next-line
          header.sections = header.sections.filter((section) => {
            return this.isActiveColumn(section.key);
          });

          if (header.sections.length === 0) return res;
        }

        res.push(header);

        return res;
      }, []);
    },
    noEdit() {
      return !this.$can('car_accident_edit') || this.simple;
    },
    statuses() {
      const list = this.properties.car_statuses;

      if (!list) return [];

      return [{ name: this.$t('common.no-selected'), id: null }, ...list];
    },
  },
  methods: {
    formatAmount,
    changeDate(val) {
      if (val.length === 2) {
        this.$refs.menuDate.save(this.filters.dates);
      }
    },
    getTypeClass(type) {
      switch (type) {
        case 0:
          return 'red lighten-4';
        case 1:
          return 'green lighten-4';
        case 2:
          return 'yellow lighten-4';
        default:
          return null;
      }
    },
    allowedDates(value) {
      const [from, to] = this.filters.dates;

      if (!from) return true;
      if (to) return true;

      const firstDate = new Date(from);
      const nextDate = new Date(value);

      return nextDate >= firstDate;
    },
    getType(id) {
      if (!id && id !== 0) return '—';

      const type = this.properties.selects.find((item) => item.id === id);
      return type.name;
    },
    getAccidentCauser(id) {
      if (!id && id !== 0) return '—';

      const causer = this.properties.accident_causers.find(
        (item) => item.id === id,
      );
      return causer.name;
    },
    getStatus(id) {
      if (!id && id !== 0) return '—';

      const status = this.properties.car_statuses.find(
        (item) => item.id === id,
      );
      return status.name;
    },
    getInsuranceType(id) {
      if (!id && id !== 0) return '—';

      const insurance = this.properties.insurances.find(
        (item) => item.id === id,
      );
      return insurance.name;
    },
    getRestorationType(id) {
      if (!id && id !== 0) return '—';

      const restoration = this.properties.repair_types.find(
        (item) => item.id === id,
      );
      return restoration.name;
    },
    getRestorationStatus(id) {
      if (!id && id !== 0) return '—';

      const restoration = this.properties.auto_restoration_statuses.find(
        (item) => item.id === id,
      );
      return restoration.name;
    },
    async fetchList(params) {
      await this.$store.dispatch('dtp/fetchList', {
        ...params,
        car_id: this.carId,
        driver_id: this.driverId,
      });
    },
    async fetchProperties() {
      this.propertiesLoading = true;
      await this.$store.dispatch('dtp/fetchProperties');
      this.propertiesLoading = false;
    },
    async onPageChange(page) {
      this.loading = true;
      await this.fetchList({ ...this.filters, page });
      this.loading = false;
    },
    async updateItem({ id, params }) {
      this.updatingItems.push(id);

      const { success } = await this.$store.dispatch('dtp/updateItem', {
        id,
        params,
      });

      const snack = {
        opened: true,
        color: 'error',
        message: this.$t('common.errors.APIerror'),
      };

      if (success) {
        snack.color = 'success';
        snack.message = this.$t('common.saved');

        await this.fetchList({ ...this.filters, page: this.currentPage });
      }

      this.snack = snack;
      this.updatingItems = this.updatingItems.filter((item) => item !== id);
    },
    isActiveColumn(key) {
      if (!this.visibleColumns) return true;
      return key ? this.visibleColumns.includes(key) : true;
    },
    isStatusDisabled({ id }) {
      const nullSelected = this.filters.status[0] === null;
      const isNullItem = id === null;
      if (nullSelected && !isNullItem) return true;

      return false;
    },
    onStatusChange(statuses) {
      if (statuses.includes(null)) {
        this.filters.status = [null];
      } else {
        this.filters.status = statuses;
      }
    },
  },
  async created() {
    this.loading = true;
    const propertiesLoaded = Object.keys(this.properties).length;

    if (!propertiesLoaded) {
      await this.fetchProperties();
    }

    await this.fetchList();

    this.loading = false;
  },
  mounted() {
    const table = this.$refs['dtp-table'];
    this.tableHeight = calcTableHeight(table.$el);
  },
  beforeDestroy() {
    this.$store.commit('dtp/setListData', {
      data: [],
      current_page: 1,
      last_page: 1,
      per_page: 1,
    });
  },
};
</script>

<style lang="scss">
.dtp-table {
  width: 100%;
  display: flex;
  flex-direction: column;

  &__list {
    .updating {
      opacity: 0.6;
      pointer-events: none;
    }

    thead {
      position: sticky;
      top: 0;
      z-index: 1;
    }

    td {
      background-color: #fff;
      cursor: auto;
    }

    tr > td:first-child,
    tr > td:nth-child(2),
    tr > td:nth-child(3),
    tr > td:nth-child(4) {
      background-color: #f8f8f8;
      border-color: #e8e8e8 !important;
    }

    tr > th:first-child,
    tr > td:first-child {
      position: sticky;
      left: 0;
    }

    tr > th:nth-child(2),
    tr > td:nth-child(2) {
      position: sticky;
      left: 60px;
    }

    tr > th:nth-child(3),
    tr > td:nth-child(3) {
      position: sticky;
      left: 120px;
    }

    tr > th:nth-child(4),
    tr > td:nth-child(4) {
      position: sticky;
      left: 315px;
      border-right: 2px solid #dddddd;
    }

    tr:nth-child(2) > th:nth-child(-n + 3) {
      z-index: 1;
    }

    .v-select__selection,
    .v-text-field__slot input {
      font-size: 12px;
    }

    .edit-btn {
      text-align: left;
      font-size: 14px;
      font-weight: 600;
      color: #464646;
      line-height: 1.3;

      &[disabled] {
        font-weight: normal;
      }

      &:not([disabled]):hover {
        opacity: 0.7;
      }
    }
  }

  .v-data-table-header {
    tr:first-child th {
      height: 30px !important;
      font-weight: 600 !important;
      text-align: center !important;

      &.section {
        border-right: 1px dashed rgba(255, 255, 255, 0.5);
        border-bottom: 1px dashed rgba(255, 255, 255, 0.5);
      }
    }
  }

  th {
    height: 35px !important;

    &.section {
      background-color: lighten(#424242, 10%) !important;

      &.last {
        border-right: 1px dashed rgba(255, 255, 255, 0.5);
      }
    }
  }

  .v-data-table__progress th {
    height: 2px !important;
  }

  &__filters {
    display: flex;
  }

  &__sort-btn {
    transition: transform 0.3s;

    &.desc {
      transform: rotate(180deg);
    }
  }

  &__search {
    display: flex;
    max-width: 450px;
    width: 100%;

    &-field {
      width: 100%;
    }
  }

  &__dates,
  &__status {
    max-width: 250px;
    margin-left: 35px;
  }

  &__status {
    .v-select__slot input {
      margin-top: 0 !important;
      margin-bottom: 0 !important;
    }
  }

  th {
    white-space: nowrap;
  }

  .v-pagination {
    button {
      box-shadow: none !important;
    }
  }
}
</style>
